/**
 * The death screen — docs/23.
 *
 * **What makes this different from every other panel in `src/ui`: the world is
 * still running behind it.** Noita's death screen is drawn over a live game and
 * that is not decoration — the corpse is still in the lava, the fire the player
 * started is still spreading, and the last frame of the run keeps being played
 * while they read why it ended. So this is a `Screen` in the modal layer and
 * not an `AppScene`, `UIRoot` suppresses the scrim for it alone, and the panel
 * is a column against one edge rather than a box in the middle: the middle is
 * where the body is.
 *
 * **One phase, diverging from the reference on purpose.** Noita's death is two
 * screens (docs/23 §2, measured): two lines over the live world, and the
 * statistics only when the player asks. The user saw that built and asked for
 * one screen with the cause as its title — *"no need to make this two steps"* —
 * so the prompt phase is gone, not skipped. What survives from §2 is the half
 * that mattered: Noita's box is inset 40 % from the top and 32 % from each side
 * by named constants in `magic_numbers.xml`, so the commitment to not covering
 * the world is *authored*, not incidental. Ours is a left column for the same
 * reason and by our own choice (docs/23 §5).
 *
 * Three properties that follow, each load-bearing:
 *
 *   - **It renders once.** `update(vm)` is called at the instant of death and
 *     never again; there is no per-frame path through this file at all. The
 *     frame budget's slack is 0.3 ms (#75) and a sim that keeps stepping already
 *     costs what it always cost — the overlay must add nothing on top of it.
 *   - **It cannot be dismissed.** No Escape, no B button, no close affordance.
 *     `UIRoot` and `menuCommandFor` both refuse; this file simply has no such
 *     control to press.
 *   - **It shows a snapshot.** Every string here was fixed when the run ended.
 *     The log below is a copy, because `Game.events` is a 64-line ring buffer
 *     the running simulation keeps pushing into.
 *
 * Follows `title.ts` exactly otherwise: renders a view model, reports intent
 * through callbacks, mutates nothing, imports only the DOM kit. The user's note
 * on the menus is the register this is written in.
 */

import { el, injectStyle } from './style';
import type { GameOverAction, GameOverVM } from './viewModels';

const STYLE_ID = 'noitroidvania-gameover-style';

/**
 * Its own stylesheet, on the same argument `shellStyle.ts` makes: a run that
 * never ends should not carry a byte of CSS for the screen that ends it. Every
 * colour is a `.ui-root` custom property, so the high-contrast setting and the
 * per-biome grading both reach this screen without knowing it exists.
 */
const GAMEOVER_CSS = /* css */ `
.go {
  position: absolute; inset: 0;
  display: grid;
  /* Left column, not centred. The corpse is in the middle of the screen and
     the whole point of this panel is that you can still see it. */
  grid-template-columns: min(560px, 92vw) 1fr;
  align-items: stretch;
  pointer-events: none;
}

.go__col {
  pointer-events: auto;
  overflow-y: auto;
  padding: 26px 26px 22px;
  display: flex; flex-direction: column; gap: 16px;
  background: linear-gradient(90deg, rgb(from var(--ui-scrim) r g b / .94) 0%, rgb(from var(--ui-scrim) r g b / .88) 72%, rgb(from var(--ui-scrim) r g b / 0) 100%);
}

/* ---- the verdict ---- */
.go__title {
  margin: 0;
  font-size: clamp(20px, 4.4vw, 34px);
  letter-spacing: .34em; text-indent: .34em;
  text-transform: uppercase; font-weight: normal;
  color: var(--ui-hp);
  text-shadow: 0 0 24px rgb(from var(--ui-hp) r g b / .28);
}
.go__rule { height: 1px; background: linear-gradient(90deg, var(--ui-line-hot), transparent); }
.go__cause {
  color: var(--ui-fg); font-size: calc(var(--ui-fs) * 1.15);
  letter-spacing: .1em;
}
.go__cause-detail { color: var(--ui-fg-mute); font-size: calc(var(--ui-fs) * .86); }
.go__epitaph {
  color: var(--ui-fg-dim); font-size: calc(var(--ui-fs) * .86);
  letter-spacing: .1em; text-transform: uppercase;
}

/* ---- sections ---- */
.go__sec { display: flex; flex-direction: column; gap: 3px; }
.go__sec > h2 {
  margin: 0 0 4px; font-size: calc(var(--ui-fs) * .84); font-weight: normal;
  letter-spacing: .3em; text-transform: uppercase; color: var(--ui-accent);
  border-bottom: 1px solid var(--ui-line); padding-bottom: 5px;
}
.go__row {
  display: grid; grid-template-columns: 1fr auto;
  gap: 1px 14px; align-items: baseline;
  padding: 2px 0;
}
.go__row > b {
  font-weight: normal; color: var(--ui-fg-dim);
  letter-spacing: .12em; text-transform: uppercase;
  font-size: calc(var(--ui-fs) * .88);
}
.go__row > span { color: var(--ui-fg); justify-self: end; white-space: nowrap; }
.go__row[data-tone="good"] > span { color: var(--ui-good); }
.go__row[data-tone="bad"]  > span { color: var(--ui-bad); }
.go__row > i {
  grid-column: 1 / -1; font-style: normal;
  color: var(--ui-fg-mute); font-size: calc(var(--ui-fs) * .82); line-height: 1.5;
}
.go__empty {
  color: var(--ui-fg-mute); font-size: calc(var(--ui-fs) * .84);
  font-style: italic; padding: 2px 0;
}

/* ---- the log ---- */
.go__log {
  display: flex; flex-direction: column; gap: 1px;
  border-left: 1px solid var(--ui-line); padding-left: 12px;
  font-size: calc(var(--ui-fs) * .84);
}
.go__log > div { color: var(--ui-fg-dim); }
/* The last line is the death. It is the sentence the whole screen is about. */
.go__log > div:last-child { color: var(--ui-fg); }

/* ---- actions ---- */
.go__acts { display: flex; flex-direction: column; gap: 4px; margin-top: auto; padding-top: 8px; }
.go__act {
  display: flex; align-items: baseline; gap: 12px;
  padding: 9px 14px;
  border: 1px solid transparent;
  background: rgb(from var(--ui-panel) r g b / .55);
  cursor: pointer; text-align: left; width: 100%;
  font: inherit; color: var(--ui-fg-dim);
}
.go__act b {
  font-weight: normal; letter-spacing: .2em; text-transform: uppercase;
  color: var(--ui-fg); min-width: 13ch;
}
.go__act i { font-style: normal; color: var(--ui-fg-mute); font-size: calc(var(--ui-fs) * .88); }
.go__act[data-on="1"] { border-color: var(--ui-accent); background: var(--ui-panel-2); }
.go__act[data-on="1"] b { color: var(--ui-accent); }
.go__act:hover, .go__act:focus { border-color: var(--ui-line-hot); outline: none; }
.go__foot {
  display: flex; gap: 18px;
  color: var(--ui-fg-mute); font-size: calc(var(--ui-fs) * .8);
  letter-spacing: .12em; text-transform: uppercase;
}
`;

/** Idempotent, exactly like `injectStyle` and `injectShellStyle`. */
export function injectGameOverStyle(doc: Document = document): void {
  if (doc.getElementById(STYLE_ID)) return;
  const s = doc.createElement('style');
  s.id = STYLE_ID;
  s.textContent = GAMEOVER_CSS;
  doc.head.appendChild(s);
}

export interface GameOverCallbacks {
  onChoose?(action: GameOverAction): void;
}

export class GameOverScreen {
  readonly root: HTMLElement;

  private cb: GameOverCallbacks;


  private titleEl: HTMLElement;
  private causeEl: HTMLElement;
  private causeDetailEl: HTMLElement;
  private epitaphEl: HTMLElement;
  private secHost: HTMLElement;
  private logHost: HTMLElement;
  private actHost: HTMLElement;

  private actions: readonly { id: GameOverAction }[] = [];
  private actEls: HTMLElement[] = [];
  private cursor = 0;

  constructor(cb: GameOverCallbacks = {}) {
    this.cb = cb;
    injectStyle();
    injectGameOverStyle();

    this.root = el('div', 'go');
    this.root.tabIndex = -1;
    this.root.hidden = true;
    this.root.setAttribute('role', 'dialog');
    this.root.setAttribute('aria-modal', 'true');
    this.root.setAttribute('aria-label', 'Run over');

    const col = el('div', 'go__col');

    this.titleEl = document.createElement('h1');
    this.titleEl.className = 'go__title';
    this.causeEl = el('div', 'go__cause');
    this.causeDetailEl = el('div', 'go__cause-detail');
    this.epitaphEl = el('div', 'go__epitaph');

    this.secHost = el('div');
    this.secHost.style.display = 'flex';
    this.secHost.style.flexDirection = 'column';
    this.secHost.style.gap = '14px';

    this.logHost = el('div', 'go__log');
    this.actHost = el('div', 'go__acts');

    const foot = el('div', 'go__foot');
    foot.append(
      el('span', '', '↑↓ choose'),
      el('span', '', 'Enter confirm'),
      el('span', '', 'Pad: d-pad, A'),
    );

    col.append(
      this.titleEl,
      el('div', 'go__rule'),
      this.causeEl,
      this.causeDetailEl,
      // Continue and Quit sit directly under the cause, above everything the
      // player might read. The postmortem is long and the two things they may
      // want to do immediately should not be at the bottom of it — the stats
      // are for reading, not for scrolling past.
      this.actHost,
      this.epitaphEl,
      this.secHost,
      this.logHost,
      foot,
    );
    // The right-hand cell is deliberately empty and deliberately not
    // pointer-transparent-by-accident: `.go` sets `pointer-events: none` and
    // only `.go__col` takes them back, so a click on the visible world does not
    // land on an invisible panel.
    this.root.append(col);
  }

  /**
   * Draw the postmortem. Called **once**, when the run ends.
   *
   * A full rebuild rather than a diff, because it happens once per run and the
   * `dataset.sig` idiom the live panels use buys nothing against a single call.
   */
  update(vm: GameOverVM): void {
    this.titleEl.textContent = vm.title;

    this.causeEl.textContent = vm.cause ?? '';
    this.causeEl.hidden = vm.cause === null;
    this.causeDetailEl.textContent = vm.causeDetail ?? '';
    this.causeDetailEl.hidden = vm.causeDetail === null;
    this.epitaphEl.textContent = vm.epitaph;

    this.secHost.textContent = '';
    for (const sec of vm.sections) {
      const s = el('div', 'go__sec');
      const h = document.createElement('h2');
      h.textContent = sec.title;
      s.append(h);
      if (sec.rows.length === 0) {
        s.append(el('div', 'go__empty', sec.empty));
      } else {
        for (const r of sec.rows) {
          const row = el('div', 'go__row');
          if (r.tone && r.tone !== 'plain') row.dataset.tone = r.tone;
          row.append(el('b', '', r.label), el('span', '', r.value));
          if (r.note) row.append(el('i', '', r.note));
          s.append(row);
        }
      }
      this.secHost.append(s);
    }

    this.logHost.textContent = '';
    this.logHost.hidden = vm.log.length === 0;
    for (const line of vm.log) this.logHost.append(el('div', '', line));

    this.actHost.textContent = '';
    this.actEls = [];
    this.actions = vm.actions;
    for (const a of vm.actions) {
      const b = document.createElement('button');
      b.className = 'go__act';
      b.type = 'button';
      b.append(el('b', '', a.label), el('i', '', a.note));
      b.addEventListener('click', () => this.cb.onChoose?.(a.id));
      b.addEventListener('mousemove', () => {
        const i = this.actions.findIndex((x) => x.id === a.id);
        if (i >= 0 && i !== this.cursor) {
          this.cursor = i;
          this.paint();
        }
      });
      this.actHost.append(b);
      this.actEls.push(b);
    }
    if (this.cursor >= this.actEls.length) this.cursor = 0;
    this.paint();
  }

  private paint(): void {
    for (let i = 0; i < this.actEls.length; i++) {
      const on = i === this.cursor ? '1' : '0';
      if (this.actEls[i].dataset.on !== on) this.actEls[i].dataset.on = on;
    }
  }

  /**
   * Arrow keys and Enter. Returns true when the key was consumed.
   *
   * **Escape is deliberately not handled and must not be**, here or in
   * `UIRoot`: it is the key that closes every other panel, and the run is over.
   * Returning false for it would be equally wrong if the caller then treated
   * "unhandled" as "close" — `UIRoot`'s handler is written to check the screen
   * first, and `tests/gameOver.test.ts` pins that.
   */
  handleKey(e: KeyboardEvent): boolean {
    const n = this.actEls.length;
    if (n === 0) return false;
    if (e.code === 'ArrowDown' || e.code === 'KeyS') {
      this.cursor = (this.cursor + 1) % n;
    } else if (e.code === 'ArrowUp' || e.code === 'KeyW') {
      this.cursor = (this.cursor - 1 + n) % n;
    } else if (e.code === 'Enter' || e.code === 'Space' || e.code === 'NumpadEnter') {
      this.cb.onChoose?.(this.actions[this.cursor].id);
    } else {
      return false;
    }
    this.paint();
    return true;
  }

  /**
   * Entering the screen. The cursor goes back to the top — a second death must
   * not inherit the row the first one left selected.
   */
  onShow(): void {
    this.cursor = 0;
    this.paint();
    this.root.focus();
  }

  destroy(): void {
    this.root.remove();
  }
}
